import React from "react";
import "./LineChartParts.css";
import { t } from "../../../locales/translate";

/**
 * Hover card for the call-average line chart.
 *
 * Differs from the shared chart tooltip in three ways that matter when an
 * employee carries a long product list: only the products with calls that day
 * are listed, those are ranked by value instead of series order, and the day's
 * total is spelled out so the reader does not have to add the lines up by eye.
 *
 * Listing every product meant a day with three calls filled the card with
 * zeroes and pushed the three that mattered behind a scrollbar.
 */
const LineChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const rows = payload
    .filter((item) => Number(item.value) > 0)
    .slice()
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  const total = rows.reduce((sum, item) => sum + (item.value || 0), 0);
  /* The axis tick is abbreviated to fit; the hover has room to spell the
     period out, so a bar labelled "Mar 25" reads "March 2025" here. */
  const heading = payload[0]?.payload?.fullLabel || label;

  /* Most days in a filled range are quiet ones. Hovering one should still say
     something rather than flash an empty card. */
  if (!rows.length) {
    return (
      <div className="lc-tooltip" role="tooltip">
        <p className="lc-tooltip__date">{heading}</p>
        <p className="lc-tooltip__quiet">{t("noCallsLogged")}</p>
      </div>
    );
  }

  return (
    <div className="lc-tooltip" role="tooltip">
      <p className="lc-tooltip__date">{heading}</p>
      <div className="lc-tooltip__rows">
        {rows.map((item) => (
          <div className="lc-tooltip__row" key={item.dataKey ?? item.name}>
            <span
              className="lc-tooltip__swatch"
              style={{ backgroundColor: item.color }}
              aria-hidden="true"
            />
            <span className="lc-tooltip__name">{item.name}</span>
            <span className="lc-tooltip__value">{item.value ?? 0}</span>
          </div>
        ))}
      </div>
      {rows.length > 1 && (
        <div className="lc-tooltip__total">
          <span className="lc-tooltip__name">{t("totalCalls")}</span>
          <span className="lc-tooltip__value">{total}</span>
        </div>
      )}
    </div>
  );
};

export default LineChartTooltip;
